import { apiFetcher } from '@/api/api-fetcher'
import { etagManager } from '@/utils/etag-manager'
import type { UserResponse } from '@/types/api/responses'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function validateEmail(email: string, fieldName = 'email'): void {
  if (!email || !EMAIL_REGEX.test(email)) {
    throw new Error(`Invalid ${fieldName} format`)
  }
}

export async function createUser(request: {
  email: string
  fullName: string
}): Promise<UserResponse> {
  const response = await apiFetcher.securePost<UserResponse>('/users', request)
  if (!response) {
    throw new Error('Failed to create user')
  }
  return response
}

/**
 * ETag対応のユーザー情報更新
 * If-Matchヘッダーによる楽観的ロックを実装
 * 412エラー時の自動リトライ機能付き
 */
export async function updateUser(
  email: string,
  updates: Partial<{
    fullName: string
    avatar?: string
    notificationEmail?: string | null
  }>,
  maxRetries: number = 3
): Promise<UserResponse> {
  validateEmail(email)
  const payload: typeof updates = { ...updates }

  if (payload.notificationEmail !== undefined) {
    if (payload.notificationEmail === null) {
      // Use account email fallback
    } else if (typeof payload.notificationEmail === 'string') {
      const normalized = payload.notificationEmail.trim()
      if (normalized.length === 0) {
        payload.notificationEmail = null
      } else {
        validateEmail(normalized, 'notificationEmail')
        payload.notificationEmail = normalized
      }
    }
  }

  // Use consistent resource key for current user operations
  const resourceKey = 'user:me'

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // ETagが必要なため、まず最新のユーザー情報を取得
      await getCurrentUser()

      // ETag付きで更新リクエストを送信
      const response = await apiFetcher.securePut<UserResponse>(`/users/${email}`, payload, {
        requireETag: true,
        resourceKey,
      })

      if (!response) {
        throw new Error('Failed to update user')
      }

      return response
    } catch (error) {
      if (etagManager.isETagConflictError(error)) {
        // ETagコンフリクトの場合
        if (attempt < maxRetries) {
          // Clear cached ETag and retry
          etagManager.clearETag(resourceKey)
          // ETag conflict detected, retrying
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)))
          continue
        }
        // Max retries reached, throw user-friendly error
        throw new Error(
          'The user information was updated by another process. Please refresh the page and try again.'
        )
      }
      throw error
    }
  }

  // Should never reach here
  throw new Error('Failed to update user after maximum retries')
}

/**
 * ETag対応のユーザーメールアドレス更新
 * If-Matchヘッダーによる楽観的ロックを実装
 * 412エラー時の自動リトライ機能付き
 */
export async function updateUserEmail(
  currentEmail: string,
  newEmail: string,
  maxRetries: number = 3
): Promise<UserResponse> {
  validateEmail(currentEmail, 'currentEmail')
  validateEmail(newEmail, 'newEmail')

  // Use consistent resource key for current user operations
  const resourceKey = 'user:me'

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // ETagが必要なため、まず最新のユーザー情報を取得
      await getCurrentUser()

      // ETag付きでメールアドレス更新リクエストを送信
      const response = await apiFetcher.securePut<UserResponse>(
        `/users/${currentEmail}/email`,
        { newEmail },
        {
          requireETag: true,
          resourceKey,
        }
      )

      if (!response) {
        throw new Error('Failed to update email')
      }

      return response
    } catch (error) {
      if (etagManager.isETagConflictError(error)) {
        // ETagコンフリクトの場合
        if (attempt < maxRetries) {
          // Clear cached ETag and retry
          etagManager.clearETag(resourceKey)
          // ETag conflict detected, retrying
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)))
          continue
        }
        // Max retries reached, throw user-friendly error
        throw new Error(
          'The user information was updated by another process. Please refresh the page and try again.'
        )
      }
      throw error
    }
  }

  // Should never reach here
  throw new Error('Failed to update email after maximum retries')
}

/**
 * ユーザー削除（管理者権限必要）
 */
export async function deleteUser(email: string): Promise<void> {
  validateEmail(email, 'email')

  return apiFetcher.secureDelete<void>(`/users/${email}`)
}

/**
 * 現在のユーザー情報を取得
 * ✅ SECURE: Self-access only, uses /users/me endpoint
 */
export async function getCurrentUser(): Promise<UserResponse> {
  return apiFetcher.secureGet<UserResponse>('/users/me', {
    resourceKey: 'user:me',
  })
}

/**
 * ユーザーのパスワード変更リクエスト
 */
export async function requestPasswordReset(email: string): Promise<void> {
  validateEmail(email, 'email')

  return apiFetcher.securePost<void>('/users/password-reset', { email })
}

/**
 * Firebase UID移行処理（既存ユーザー向け）
 * 412エラー時の自動リトライ機能付き
 */
export async function updateUserFirebaseUid(
  email: string,
  firebaseUid: string,
  maxRetries: number = 3
): Promise<UserResponse> {
  validateEmail(email, 'email')

  if (!firebaseUid || firebaseUid.trim() === '') {
    throw new Error('Firebase UID is required')
  }

  // Use consistent resource key for current user operations
  const resourceKey = 'user:me'

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // ETagが必要なため、まず最新のユーザー情報を取得
      await getCurrentUser()

      // ETag付きでFirebase UID更新リクエストを送信
      const response = await apiFetcher.securePut<UserResponse>(
        `/users/${email}/firebase-uid`,
        { firebaseUid },
        {
          requireETag: true,
          resourceKey,
        }
      )

      if (!response) {
        throw new Error('Failed to update Firebase UID')
      }

      return response
    } catch (error) {
      if (etagManager.isETagConflictError(error)) {
        // ETagコンフリクトの場合
        if (attempt < maxRetries) {
          // Clear cached ETag and retry
          etagManager.clearETag(resourceKey)
          // ETag conflict detected, retrying
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)))
          continue
        }
        // Max retries reached, throw user-friendly error
        throw new Error(
          'The user information was updated by another process. Please refresh the page and try again.'
        )
      }
      throw error
    }
  }

  // Should never reach here
  throw new Error('Failed to update Firebase UID after maximum retries')
}

/**
 * セキュアなメールアドレス存在チェック（Firebase Admin SDK使用）
 *
 * Security Note: This function uses the backend's Firebase Admin SDK to securely
 * check if an email is already registered, preventing client-side enumeration attacks.
 * Only returns boolean existence status, no user details.
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  validateEmail(email, 'email')

  try {
    const response = await apiFetcher.securePost<{ exists: boolean }>('/users/check-email-exists', {
      email: email.trim().toLowerCase(),
    })

    return response?.exists ?? false
  } catch (error) {
    console.error('Error checking email existence:', error)
    // For security reasons, return false on any error to prevent information leakage
    return false
  }
}

/**
 * ユーザーのETagをクリア（ログアウト時など）
 */
export function clearUserCache(email?: string): void {
  if (email) {
    const resourceKey = etagManager.getUserResourceKey(email)
    etagManager.clearETag(resourceKey)
  } else {
    etagManager.clearAllETags()
  }
}
